import React from 'react';
import { SparklesIcon, CheckIcon, TrendingUpIcon } from 'lucide-react';
import { CDIsotype } from './BrandMark';
export type ProductScene = 'megapack' | 'ai' | 'templates' | 'analytics';
type ProductCoverProps = {
  title: string;
  kicker: string;
  scene: ProductScene;
  variant?: 'card' | 'featured';
};
const SCENE_GLOW: Record<ProductScene, string> = {
  megapack: 'radial-gradient(70% 70% at 28% 10%, rgba(37,99,246,0.24), transparent 62%), radial-gradient(70% 70% at 80% 90%, rgba(139,92,246,0.24), transparent 62%)',
  ai: 'radial-gradient(75% 75% at 70% 12%, rgba(139,92,246,0.26), transparent 62%), radial-gradient(65% 65% at 18% 88%, rgba(37,99,246,0.20), transparent 62%)',
  templates: 'radial-gradient(70% 70% at 50% 0%, rgba(37,99,246,0.24), transparent 60%), radial-gradient(60% 60% at 85% 95%, rgba(139,92,246,0.18), transparent 62%)',
  analytics: 'radial-gradient(70% 70% at 20% 20%, rgba(139,92,246,0.22), transparent 62%), radial-gradient(80% 80% at 90% 80%, rgba(37,99,246,0.24), transparent 62%)'
};

/* ── shared atoms ───────────────────────────────────────────────────────── */

function Line({
  w,
  dim = false
}: {
  w: string;
  dim?: boolean;
}) {
  return <span className={`block h-[3px] rounded-full ${dim ? 'bg-white/12' : 'bg-white/25'}`} style={{
    width: w
  }} data-id="element-337" data-mp="1r3jocqk5ihww" />;
}
function Glass({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`rounded-xl border border-white/[0.10] bg-white/[0.055] backdrop-blur-md shadow-[0_20px_50px_-26px_rgba(0,0,0,0.95)] ${className}`} data-id="element-338" data-mp="yh8nrfwjoh03">
      {children}
    </div>;
}
function BookCover({
  title,
  kicker,
  width,
  rotate = -13
}: {
  title: string;
  kicker: string;
  width: string;
  rotate?: number;
}) {
  return <div className="relative" style={{
    width,
    transform: `perspective(1100px) rotateY(${rotate}deg) rotateX(3deg)`
  }} data-id="element-339" data-mp="2jta5l8h2xzx8">
      <div className="relative overflow-hidden rounded-[10px] border border-white/[0.10] bg-[#0e0e13] shadow-[0_44px_80px_-34px_rgba(0,0,0,1)]" data-id="element-340" data-mp="2nwf5d39rzni6">
        <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[7px] bg-gradient-to-b from-white/[0.14] via-white/[0.04] to-transparent" data-id="element-341" data-mp="jrak13zjjwhv" />
        <div className="relative flex aspect-[3/4] flex-col justify-between p-[9%]" data-id="element-342" data-mp="srfgwk73zchs">
          <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[42%]" style={{
          background: 'radial-gradient(120% 100% at 50% 130%, rgba(139,92,246,0.42), transparent 65%)'
        }} data-id="element-343" data-mp="spcqhz21nhcj" />
          <svg aria-hidden="true" viewBox="0 0 100 40" preserveAspectRatio="none" className="absolute inset-x-0 bottom-0 h-[38%] w-full opacity-80" data-id="element-344" data-mp="espld8zxtkvj">
            <defs data-id="element-345" data-mp="3ieersvppi87a">
              <linearGradient id="pcWave" x1="0" y1="0" x2="1" y2="0" data-id="element-346" data-mp="3dhepmvvz9plg">
                <stop offset="0%" stopColor="#2563FF" data-id="element-347" data-mp="3dfbz8aqwxug7" />
                <stop offset="100%" stopColor="#8B5CF6" data-id="element-348" data-mp="1sz4795ib43kt" />
              </linearGradient>
            </defs>
            {[0, 5, 10].map(o => <path key={o} d={`M0 ${26 + o} C 22 ${12 + o}, 38 ${34 + o}, 62 ${20 + o} S 88 ${6 + o}, 100 ${16 + o}`} fill="none" stroke="url(#pcWave)" strokeWidth="0.9" strokeOpacity={o === 0 ? 0.9 : 0.4} data-id="element-349" data-mp="1xs71zgytlj2l" />)}
          </svg>

          <CDIsotype className="relative w-[34%]" data-id="element-350" data-mp="2gp88x7bxpt96" />

          <div className="relative" data-id="element-351" data-mp="2gmub4qpve2xx">
            <p className="text-[6.5px] font-medium uppercase tracking-[0.22em] text-white/45" data-id="element-352" data-mp="22pp0sej4ihwv">
              {kicker}
            </p>
            <p className="mt-1.5 text-[11px] font-semibold uppercase leading-tight tracking-[0.02em] text-white" data-id="element-353" data-mp="27mp5al0brclv">
              {title}
            </p>
            <span className="mt-2.5 block h-[2px] w-[38%] rounded-full brand-gradient-bg" data-id="element-354" data-mp="w8tt9l4pvjyv" />
          </div>
        </div>
      </div>
      <div aria-hidden="true" className="mx-auto mt-3 h-8 w-[86%] rounded-full blur-xl" style={{
      background: 'radial-gradient(60% 100% at 50% 0%, rgba(139,92,246,0.35), transparent 70%)'
    }} data-id="element-355" data-mp="1bjs0psqiscm0" />
    </div>;
}

/* ── scenes ─────────────────────────────────────────────────────────────── */

function MegapackScene({
  title,
  kicker
}: {
  title: string;
  kicker: string;
}) {
  return <>
      <BookCover title={title} kicker={kicker} width="44%" data-id="element-356" data-mp="1uf9sxwxmmjsx" />

      <Glass className="absolute right-[6%] top-[12%] w-[27%] p-2" data-id="element-357" data-mp="1qgit3i1s0o24">
        <div className="flex items-center gap-1.5" data-id="element-358" data-mp="1lg4vktxswwic">
          <span className="grid h-3.5 w-3.5 place-items-center rounded-md brand-gradient-bg" data-id="element-359" data-mp="1lj0scexwxf57">
            <SparklesIcon className="h-2 w-2 text-white" aria-hidden="true" data-id="element-360" data-mp="1gjxnauhxn8x8" />
          </span>
          <Line w="60%" data-id="element-361" data-mp="a32gy4f9ih0t" />
        </div>
        <span className="mt-2 block h-[3px] w-full rounded-full brand-gradient-bg opacity-80" data-id="element-362" data-mp="52xulxroas2o" />
        <span className="mt-1.5 block h-[3px] w-[55%] rounded-full bg-white/12" data-id="element-363" data-mp="3r4dnbzcfvsej" />
      </Glass>

      <Glass className="absolute bottom-[10%] left-[6%] w-[28%] p-2" data-id="element-364" data-mp="3w387b4m57n08">
        <div className="space-y-1.5" data-id="element-365" data-mp="3w142mkgf6jyw">
          {[0, 1, 2].map(i => <div key={i} className="flex items-center gap-1.5" data-id="element-366" data-mp="4w5fcayhdcoy">
              <span className="grid h-3 w-3 place-items-center rounded-[4px] brand-gradient-bg" data-id="element-367" data-mp="9o5kchteqvse">
                <CheckIcon className="h-2 w-2 text-white" strokeWidth={3} aria-hidden="true" data-id="element-368" data-mp="dpqrdmj9gfuo" />
              </span>
              <Line w={i === 1 ? '52%' : '72%'} dim data-id="element-369" data-mp="nnex17ien7rm" />
            </div>)}
        </div>
      </Glass>

      <Glass className="absolute right-[10%] bottom-[16%] w-[20%] p-2" data-id="element-370" data-mp="sm868nqnsclz">
        <div className="grid grid-cols-3 gap-1" data-id="element-371" data-mp="sk5g83piajh9">
          {[0, 1, 2, 3, 4, 5].map(i => <span key={i} className="block aspect-square rounded-[4px]" style={{
          background: i % 3 === 0 ? 'linear-gradient(135deg, rgba(37,99,246,0.7), rgba(37,99,246,0.15))' : i % 3 === 1 ? 'linear-gradient(135deg, rgba(139,92,246,0.7), rgba(139,92,246,0.12))' : 'rgba(255,255,255,0.06)'
        }} data-id="element-372" data-mp="2pguog6mbgqnn" />)}
        </div>
      </Glass>
    </>;
}
function AiScene() {
  return <>
      {/* prompt console */}
      <div className="relative w-[76%] overflow-hidden rounded-2xl border border-white/[0.10] bg-[#0e0e14]/85 shadow-[0_44px_80px_-34px_rgba(0,0,0,1)] backdrop-blur-md" style={{
      transform: 'perspective(1200px) rotateX(6deg)'
    }} data-id="element-373" data-mp="359ogcoecu9b7">
        <div className="flex items-center gap-1.5 border-b border-white/[0.07] px-3 py-2" data-id="element-374" data-mp="3a3sq1ff08vfz">
          <CDIsotype className="h-3 w-3" data-id="element-375" data-mp="3o3ka9xw4782f" />
          <span className="text-[6px] font-medium uppercase tracking-[0.2em] text-white/45" data-id="element-376" data-mp="3o0fzuis23g3b">
            IA Toolkit
          </span>
          <span className="ml-auto rounded-full border border-[#8b5cf6]/35 bg-[#8b5cf6]/12 px-1.5 py-[2px] text-[5.5px] font-medium text-[#b49cff]" data-id="element-377" data-mp="6k3d5mqmz6q3">
            Prompt library
          </span>
        </div>
        <div className="space-y-2 p-3" data-id="element-378" data-mp="3tp2prgoidsat">
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-2" data-id="element-379" data-mp="3to1a0ywjshmr">
            <div className="flex items-center gap-1.5" data-id="element-380" data-mp="2gx5qbrsf9i1">
              <span className="grid h-3.5 w-3.5 place-items-center rounded-md brand-gradient-bg" data-id="element-381" data-mp="3jyzxxjt4ike2">
                <SparklesIcon className="h-2 w-2 text-white" aria-hidden="true" data-id="element-382" data-mp="3op6k7d05xst5" />
              </span>
              <span className="text-[6px] font-light text-white/55" data-id="element-383" data-mp="3azmj0bm8d3ly">
                Generá 30 días de contenido…
              </span>
            </div>
          </div>
          <div className="space-y-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] p-2" data-id="element-384" data-mp="30zlzopod2nhr">
            <Line w="92%" dim data-id="element-385" data-mp="35z8cwz46w3qk" />
            <Line w="76%" dim data-id="element-386" data-mp="35yfagac9k0e8" />
            <Line w="84%" dim data-id="element-387" data-mp="3lgzvz15nfken" />
            <span className="mt-1 block h-[3px] w-[38%] rounded-full brand-gradient-bg" data-id="element-388" data-mp="3lh8cd30zd7ul" />
          </div>
          <div className="flex gap-1" data-id="element-389" data-mp="3giyeulrs9v5t">
            {['Ads', 'Email', 'Reels', 'SEO'].map(t => <span key={t} className="rounded-full border border-white/[0.10] bg-white/[0.04] px-1.5 py-[2px] text-[5.5px] font-light text-white/50" data-id="element-390" data-mp="3cf8sm2ykzpny">
                {t}
              </span>)}
          </div>
        </div>
      </div>

      {/* node graph */}
      <svg aria-hidden="true" viewBox="0 0 100 100" className="absolute left-[4%] top-[10%] h-[34%] w-[30%] opacity-80" data-id="element-391" data-mp="2xpuigsn6l47i">
        <defs data-id="element-392" data-mp="2oqzc9gk4zu47">
          <linearGradient id="aiNode" x1="0" y1="1" x2="1" y2="0" data-id="element-393" data-mp="2oriii00fcu2e">
            <stop offset="0%" stopColor="#2563FF" data-id="element-394" data-mp="2jrmp0upcsl13" />
            <stop offset="100%" stopColor="#8B5CF6" data-id="element-395" data-mp="2jqbbw4a0q0xb" />
          </linearGradient>
        </defs>
        <g stroke="url(#aiNode)" strokeWidth="1.2" opacity="0.7" data-id="element-396" data-mp="2etuml6r31gin">
          <path d="M18 78 L48 52" data-id="element-397" data-mp="uar2wux81x79" />
          <path d="M48 52 L82 30" data-id="element-398" data-mp="u9piw0y8a0de" />
          <path d="M48 52 L78 74" data-id="element-399" data-mp="ucv6tcv4ew7j" />
          <path d="M18 78 L14 40" data-id="element-400" data-mp="z92cd4jlu5oy" />
        </g>
        <g fill="url(#aiNode)" data-id="element-401" data-mp="z7r2qo3ha3px">
          <circle cx="18" cy="78" r="4" data-id="element-402" data-mp="148y7sfe8kosw" />
          <circle cx="48" cy="52" r="5" data-id="element-403" data-mp="149zuxi8j11qz" />
          <circle cx="82" cy="30" r="3.5" data-id="element-404" data-mp="146u8kara8dyy" />
          <circle cx="78" cy="74" r="3" data-id="element-405" data-mp="1898oon3kgzmj" />
          <circle cx="14" cy="40" r="3" data-id="element-406" data-mp="1885xsf68ux6m" />
        </g>
      </svg>

      <Glass className="absolute right-[4%] bottom-[10%] w-[26%] p-2" data-id="element-407" data-mp="blarhck00kc2">
        <span className="text-[5.5px] font-medium uppercase tracking-[0.18em] text-white/40" data-id="element-408" data-mp="gikrfm50kee5">
          Automatización
        </span>
        <div className="mt-1.5 flex items-center gap-1" data-id="element-409" data-mp="kjw995hnbvbs">
          {['A', 'B', 'C'].map((s, i) => <React.Fragment key={s} data-id="element-410" data-mp="pcz37eqc56si" data-mpi={i}>
              <span className="h-2.5 w-2.5 rounded-[3px] brand-gradient-bg opacity-90" data-id="element-411" data-mp="3oyw0amivzjxu" />
              {i < 2 && <span className="h-[1px] w-2 brand-gradient-bg" data-id="element-412" data-mp="3t0hkyu1nx2il" />}
            </React.Fragment>)}
        </div>
      </Glass>
    </>;
}
function TemplatesScene() {
  const cards = [{
    rotate: -12,
    x: '-34%',
    y: '4%',
    z: 10
  }, {
    rotate: -4,
    x: '-6%',
    y: '-2%',
    z: 20
  }, {
    rotate: 7,
    x: '22%',
    y: '6%',
    z: 10
  }];
  return <>
      {cards.map((c, i) => <div key={i} className="absolute w-[30%] overflow-hidden rounded-xl border border-white/[0.10] bg-[#0f0f15]/90 shadow-[0_34px_70px_-30px_rgba(0,0,0,1)] backdrop-blur-md" style={{
      transform: `translate(${c.x}, ${c.y}) rotate(${c.rotate}deg)`,
      zIndex: c.z
    }} data-id="element-413" data-mp="35190e3sl40qi" data-mpi={i}>
          <div className="aspect-[3/4] p-2.5" data-id="element-414" data-mp="2mcltrgurf3at">
            <span className="block h-[38%] w-full rounded-lg" style={{
          background: i === 1 ? 'linear-gradient(135deg, #2563FF, #8B5CF6)' : i === 0 ? 'linear-gradient(135deg, rgba(37,99,246,0.55), rgba(37,99,246,0.10))' : 'linear-gradient(135deg, rgba(139,92,246,0.55), rgba(139,92,246,0.10))'
        }} data-id="element-415" data-mp="2mbj0j29ysorq" />
            <div className="mt-2 space-y-1.5" data-id="element-416" data-mp="1m5yuuakvxbbo">
              <Line w="80%" data-id="element-417" data-mp="1h5kl8o3mxtbj" />
              <Line w="60%" dim data-id="element-418" data-mp="1h4t1cajehk13" />
              <Line w="70%" dim data-id="element-419" data-mp="1h3zr2zl8gce7" />
            </div>
            <div className="mt-2 flex items-center gap-1" data-id="element-420" data-mp="1d491gz7g02b9">
              <CDIsotype className="h-2.5 w-2.5" data-id="element-421" data-mp="1d3phze2595v4" />
              <span className="block h-[3px] w-[40%] rounded-full bg-white/12" data-id="element-422" data-mp="1855ydkb0w65x" />
            </div>
          </div>
        </div>)}

      <Glass className="absolute bottom-[8%] left-[8%] z-30 w-[26%] p-2" data-id="element-423" data-mp="13cl9mx6z4j99">
        <span className="text-[5.5px] font-medium uppercase tracking-[0.18em] text-white/40" data-id="element-424" data-mp="yg4jxyk4lwu2">
          Editable
        </span>
        <div className="mt-1.5 flex gap-1" data-id="element-425" data-mp="thtahoxqtocd">
          {['linear-gradient(135deg,#2563FF,#5b8cff)', 'linear-gradient(135deg,#8B5CF6,#b49cff)', 'rgba(255,255,255,0.9)', 'rgba(255,255,255,0.12)'].map(bg => <span key={bg} className="h-3 w-3 rounded-full border border-white/10" style={{
          background: bg
        }} data-id="element-426" data-mp="aqmrlp11mzvq" />)}
        </div>
      </Glass>
    </>;
}
function AnalyticsScene() {
  const bars = [34, 52, 44, 68, 58, 84, 74];
  return <>
      <div className="relative w-[78%] overflow-hidden rounded-2xl border border-white/[0.10] bg-[#0e0e14]/85 shadow-[0_44px_80px_-34px_rgba(0,0,0,1)] backdrop-blur-md" style={{
      transform: 'perspective(1200px) rotateX(7deg) rotateY(-4deg)'
    }} data-id="element-427" data-mp="5tealrrmt1w2">
        <div className="flex items-center justify-between border-b border-white/[0.07] px-3 py-2" data-id="element-428" data-mp="jsw2ig084v9n">
          <div className="flex items-center gap-1.5" data-id="element-429" data-mp="oqxjmve3akih">
            <CDIsotype className="h-3 w-3" data-id="element-430" data-mp="ope45709cdhr" />
            <span className="text-[6px] font-medium uppercase tracking-[0.2em] text-white/45" data-id="element-431" data-mp="1cgjifhqjuxpy">
              Playbook
            </span>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-[#2563f6]/35 bg-[#2563f6]/12 px-1.5 py-[2px] text-[5.5px] font-medium text-[#7fa3ff]" data-id="element-432" data-mp="1h92zwpscyd01">
            <TrendingUpIcon className="h-2 w-2" aria-hidden="true" data-id="element-433" data-mp="2wyatsmg3zfrc" />
            Growth
          </span>
        </div>
        <div className="grid grid-cols-[1.3fr_1fr] gap-2 p-3" data-id="element-434" data-mp="3appeqytktr11">
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-2" data-id="element-435" data-mp="36ondkcg3n4wf">
            <Line w="46%" dim data-id="element-436" data-mp="2iyt2o0fcbqet" />
            <div className="mt-2 flex h-[52px] items-end gap-[7%]" data-id="element-437" data-mp="2j045ny0you4v">
              {bars.map((h, i) => <span key={i} className="flex-1 rounded-t-[2px]" style={{
              height: `${h}%`,
              background: i % 3 === 2 ? 'linear-gradient(180deg,#8B5CF6,rgba(139,92,246,0.12))' : 'linear-gradient(180deg,#2563F6,rgba(37,99,246,0.12))'
            }} data-id="element-438" data-mp="2e1swlpihqnnp" data-mpi={i} />)}
            </div>
          </div>
          <div className="space-y-2" data-id="element-439" data-mp="xlx1o4g5e5r0">
            {['+214%', '4.8x', '12 pasos'].map(k => <div key={k} className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-2" data-id="element-440" data-mp="12kzy50i2b3nd">
                <p className="text-[8px] font-semibold leading-none text-white" data-id="element-441" data-mp="17le9areymtpm">
                  {k}
                </p>
                <span className="mt-1.5 block h-[3px] w-[62%] rounded-full brand-gradient-bg opacity-80" data-id="element-442" data-mp="1gjzku2m0l8gw" />
              </div>)}
          </div>
        </div>
      </div>

      <Glass className="absolute left-[5%] top-[12%] w-[24%] p-2" data-id="element-443" data-mp="1lgge1b82md0u">
        <span className="text-[5.5px] font-medium uppercase tracking-[0.18em] text-white/40" data-id="element-444" data-mp="1qa1c139nz701">
          Roadmap
        </span>
        <div className="mt-1.5 space-y-1.5" data-id="element-445" data-mp="1vagmnn2ka0eb">
          {[0, 1, 2].map(i => <div key={i} className="flex items-center gap-1.5" data-id="element-446" data-mp="209jrb6emq4lr">
              <span className={`h-2 w-2 rounded-full ${i === 0 ? 'brand-gradient-bg' : 'bg-white/15'}`} data-id="element-447" data-mp="24ac2w6az4ll7" />
              <Line w={i === 0 ? '70%' : '50%'} dim data-id="element-448" data-mp="3op8iwofzdyee" />
            </div>)}
        </div>
      </Glass>
    </>;
}

/* ── cover ──────────────────────────────────────────────────────────────── */

export function ProductCover({
  title,
  kicker,
  scene,
  variant = 'card'
}: ProductCoverProps) {
  return <div className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-[#08080b]" role="img" aria-label={`Mockup del producto digital ${title} con la identidad de Creativo Digital`} data-id="element-449" data-mp="3atwm8bi9lxuu">
      <div aria-hidden="true" className="absolute inset-0" style={{
      background: SCENE_GLOW[scene]
    }} data-id="element-450" data-mp="2jczvx676tbd" />
      <div aria-hidden="true" className="absolute inset-0 grid-lines opacity-40 [mask-image:radial-gradient(80%_80%_at_50%_50%,black,transparent_75%)]" data-id="element-451" data-mp="3tq5ziwlcekds" />

      {scene === 'megapack' && <MegapackScene title={title} kicker={kicker} data-id="element-452" data-mp="q7mikq8470ya" />}
      {scene === 'ai' && <AiScene data-id="element-453" data-mp="2ki31ieq3scpq" />}
      {scene === 'templates' && <TemplatesScene data-id="element-454" data-mp="2oiwqlbfa7xk7" />}
      {scene === 'analytics' && <AnalyticsScene data-id="element-455" data-mp="2oimz1jel9ice" />}

      {variant === 'featured' && <div aria-hidden="true" className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/[0.05]" data-id="element-456" data-mp="2as9s9kdfo9on" />}
    </div>;
}